import { Component, Inject, OnInit } from '@angular/core';
import { SelectionModel } from '@angular/cdk/collections';
import { MatTableDataSource } from '@angular/material/table';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ClinicalOnboardingService } from '../../services/clinical-onboarding.service';
import { ToastService } from '../../services/general/toast.service';
import { CLINICIAN_MACRO_STATUS_META, ClinicianDashboardRow, ClinicianMacroStatus } from '../../models/clinical-onboarding.model';

const READY_STATUSES: string[] = [
  ClinicianMacroStatus.SIGNED,
  ClinicianMacroStatus.INVOICE_RECEIVED,
  ClinicianMacroStatus.PAID,
];

/**
 * DBEK-395 (ridisegno "candidato"): importazione manuale dei candidati gia' pronti
 * (firmati) nel team reale della survey, disponibile in qualsiasi momento - stesso
 * risultato del trigger automatico che scatta quando la survey esce dalla bozza
 * (piano §2/§3), ma azionabile prima, per chi vuole importare in anticipo un
 * sottoinsieme gia' pronto. Tabella flat multi-select filtrabile (non lo stepper
 * Excel ne' l'accordion organigrammi di ModalImportSurveyTeamComponent/
 * ModalChartsComponent - i candidati sono gia' dati strutturati in DB, qui basta un
 * elenco), riusa la row-shape gia' esposta da ClinicalOnboardingService.getDashboard().
 */
@Component({
  selector: 'app-modal-import-clinical-onboarding-candidates',
  templateUrl: './modal-import-clinical-onboarding-candidates.component.html',
  styleUrls: ['./modal-import-clinical-onboarding-candidates.component.scss'],
  standalone: false,
})
export class ModalImportClinicalOnboardingCandidatesComponent implements OnInit {
  loading = true;
  importing = false;
  dataSource = new MatTableDataSource<ClinicianDashboardRow>([]);
  selection = new SelectionModel<ClinicianDashboardRow>(true, []);
  displayedColumns = ['select', 'name', 'macro_status'];

  readonly macroStatusMeta = CLINICIAN_MACRO_STATUS_META;

  constructor(
    public dialogRef: MatDialogRef<ModalImportClinicalOnboardingCandidatesComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { surveyId: string | number },
    private clinicalOnboardingService: ClinicalOnboardingService,
    private toastService: ToastService,
    private translateService: TranslateService,
  ) {}

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    this.clinicalOnboardingService.getDashboard(
      this.data.surveyId,
      null,
      (result) => {
        this.loading = false;
        const ready = (result.clinicians || []).filter(
          (row) => !row.imported && READY_STATUSES.includes(row.macro_status),
        );
        this.dataSource.data = ready;
        this.selection.clear();
      },
      () => {
        this.loading = false;
      },
    );
  }

  get isAllSelected(): boolean {
    return this.dataSource.data.length > 0 && this.selection.selected.length === this.dataSource.data.length;
  }

  toggleAll(): void {
    if (this.isAllSelected) {
      this.selection.clear();
    } else {
      this.selection.select(...this.dataSource.data);
    }
  }

  confirmImport(): void {
    if (this.selection.selected.length === 0) {
      return;
    }

    this.importing = true;
    const candidateIds = this.selection.selected.map((row) => row.candidate_id);
    this.clinicalOnboardingService.importCandidates(
      this.data.surveyId,
      candidateIds,
      (result) => {
        this.importing = false;
        this.toastService.success(
          this.translateService.instant('generic.success'),
          this.translateService.instant('modals.import-clinical-onboarding-candidates.success', { imported: result.imported }),
        );
        this.dialogRef.close(true);
      },
      () => {
        this.importing = false;
      },
    );
  }

  close(): void {
    this.dialogRef.close();
  }
}
